
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Language, Chef, Recipe, User, DietTypes, LifestyleTags, MealType, KnownAllergyId, Household, RecipeCategory } from '../types';
import XIcon from './icons/XIcon';
import PlusIcon from './icons/PlusIcon';
import InfoIcon from './icons/InfoIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import FilterChip from './FilterChip';
import AllergyFilterInfoModal from './AllergyFilterInfoModal';
import AllergyFilterPickerModal from './AllergyFilterPickerModal';
import DietFilterPickerModal from './DietFilterPickerModal';
import { getTranslation, isRTL } from '../utils/translations';
import {
    DIET_TYPE_CONFIG,
    PRIMARY_DIET_FILTER_KEYS,
    hasExtendedDietFilterOptions,
    toggleDietTypeFilter,
} from '../utils/dietTypes';
import { LIFESTYLE_TAG_CONFIG } from '../utils/lifestyleTags';
import { getChefDisplayName } from '../utils/chefDisplay';
import { TOP_ALLERGY_IDS, getAllergyConfig, toggleKnownAllergy } from '../utils/allergies';
import {
    aggregateHouseholdFitProfile,
    hasHouseholdFitProfile,
    mergeHouseholdFitIntoFilters,
} from '../utils/householdFitFilters';
import ChefFilterModal from './ChefFilterModal';
import {
    type RecipeFilterState,
    type RecipeVisibilityChip,
    DEFAULT_RECIPE_FILTERS,
    normalizeRecipeFilterState,
} from '../utils/recipeFilters';
import { getRecipeCategoryTranslationKey, RECIPE_CATEGORIES } from '../utils/recipeCategories';

type FilterState = RecipeFilterState;

interface RecipeFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentFilters: FilterState;
    onApply: (filters: FilterState) => void;
    language?: Language;
    chefs?: Chef[];
    recipes?: Recipe[];
    allUsers?: User[];
    activeHousehold?: Household | null;
}

type SortMode = 'all' | 'recent' | 'rated';

const MEAL_TYPE_OPTIONS: MealType[] = ['Breakfast', 'Lunch', 'Dinner'];

const VISIBILITY_CHIPS: { key: RecipeVisibilityChip; labelKey: string }[] = [
    { key: 'household', labelKey: 'currentHousehold' },
    { key: 'mine', labelKey: 'mine' },
    { key: 'shared', labelKey: 'shared' },
    { key: 'public', labelKey: 'public' },
    { key: 'favorites', labelKey: 'favorites' },
];

export const DEFAULT_FILTERS: FilterState = DEFAULT_RECIPE_FILTERS;

function sortModeFromFilters(filters: FilterState): SortMode {
    if (filters.sortBy === 'rated') return 'rated';
    if (filters.sortBy === 'recent') return 'recent';
    return 'all';
}

function applySortMode(filters: FilterState, mode: SortMode): FilterState {
    if (mode === 'rated') {
        return { ...filters, sortBy: 'rated', sortOrder: 'desc' };
    }
    return { ...filters, sortBy: 'recent', sortOrder: 'desc' };
}

function toggleInList<T extends string>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

const FilterSection: React.FC<{
    title: string;
    first?: boolean;
    className?: string;
    onInfoClick?: () => void;
    infoLabel?: string;
    children: React.ReactNode;
}> = ({ title, first, className = '', onInfoClick, infoLabel, children }) => (
    <section className={className}>
        <div className={`flex items-center gap-2 mb-3 ${first ? 'mt-2' : 'mt-6'}`}>
            <h4 className="text-sm font-bold text-slate-400 dark:text-slate-500">
                {title}
            </h4>
            {onInfoClick && (
                <button
                    type="button"
                    onClick={onInfoClick}
                    className="w-6 h-6 rounded-full bg-gray-50 dark:bg-slate-800 text-gray-400 dark:text-slate-500 flex items-center justify-center hover:text-brand-sage transition-colors shrink-0"
                    aria-label={infoLabel}
                >
                    <InfoIcon className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
        <div className="flex flex-row flex-wrap gap-2">
            {children}
        </div>
    </section>
);

const RecipeFilterModal: React.FC<RecipeFilterModalProps> = ({
    isOpen, onClose, currentFilters, onApply, language, chefs = [], recipes = [], allUsers = [], activeHousehold = null,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const rtl = isRTL(language);
    const textDir = rtl ? 'rtl' : 'ltr';

    const [filters, setFilters] = useState<FilterState>(() => normalizeRecipeFilterState(currentFilters));
    const [sortMode, setSortMode] = useState<SortMode>(() => sortModeFromFilters(normalizeRecipeFilterState(currentFilters)));
    const [isChefModalOpen, setIsChefModalOpen] = useState(false);
    const [isAllergyInfoOpen, setIsAllergyInfoOpen] = useState(false);
    const [isAllergyPickerOpen, setIsAllergyPickerOpen] = useState(false);
    const [isDietPickerOpen, setIsDietPickerOpen] = useState(false);

    const householdFitProfile = useMemo(
        () => aggregateHouseholdFitProfile(activeHousehold, allUsers),
        [activeHousehold, allUsers],
    );
    const canApplyHouseholdFit = hasHouseholdFitProfile(householdFitProfile);

    useEffect(() => {
        if (isOpen) {
            const normalized = normalizeRecipeFilterState(currentFilters);
            setFilters(normalized);
            setSortMode(sortModeFromFilters(normalized));
        }
    }, [isOpen, currentFilters]);

    if (!isOpen) return null;

    const handleApply = () => {
        onApply(applySortMode(filters, sortMode));
        onClose();
    };

    const handleClearAll = () => {
        setFilters({ ...DEFAULT_RECIPE_FILTERS });
        setSortMode('all');
    };

    const toggleDietType = (key: keyof DietTypes) => {
        setFilters((prev) => ({
            ...prev,
            dietTypes: toggleDietTypeFilter(prev.dietTypes, key),
        }));
    };

    const removeDietType = (key: keyof DietTypes) => {
        setFilters((prev) => ({
            ...prev,
            dietTypes: prev.dietTypes.filter((item) => item !== key),
        }));
    };

    const toggleMealType = (mealType: MealType) => {
        setFilters((prev) => ({
            ...prev,
            mealTypes: toggleInList(prev.mealTypes, mealType),
        }));
    };

    const toggleCategory = (category: RecipeCategory) => {
        setFilters((prev) => ({
            ...prev,
            categories: toggleInList(prev.categories, category),
        }));
    };

    const toggleLifestyleTag = (tag: keyof LifestyleTags) => {
        setFilters((prev) => ({
            ...prev,
            lifestyleTags: toggleInList(prev.lifestyleTags, tag),
        }));
    };

    const toggleVisibility = (key: RecipeVisibilityChip) => {
        setFilters((prev) => ({
            ...prev,
            visibilitySelections: toggleInList(prev.visibilitySelections, key),
        }));
    };

    const toggleExcludeAllergen = (allergyId: KnownAllergyId) => {
        setFilters((prev) => ({
            ...prev,
            excludeAllergens: toggleKnownAllergy(prev.excludeAllergens, allergyId),
        }));
    };

    const removeExcludeAllergen = (allergyId: KnownAllergyId) => {
        setFilters((prev) => ({
            ...prev,
            excludeAllergens: prev.excludeAllergens.filter((id) => id !== allergyId),
        }));
    };

    const handleHouseholdFit = () => {
        if (!canApplyHouseholdFit) return;
        setFilters((prev) => mergeHouseholdFitIntoFilters(prev, householdFitProfile));
    };

    const removeChef = (chefId: string) => {
        setFilters((prev) => ({
            ...prev,
            chefIds: prev.chefIds.filter((id) => id !== chefId),
        }));
    };

    const mealTypeLabel = (mealType: MealType) =>
        t(mealType === 'Breakfast' ? 'morning' : mealType === 'Lunch' ? 'noon' : 'evening');

    const mealTypeEmoji = (mealType: MealType) =>
        mealType === 'Breakfast' ? '🌅' : mealType === 'Lunch' ? '☀️' : '🌙';

    const dietIsAll = filters.dietTypes.length === 0;
    const extraDietTypes = filters.dietTypes.filter(
        (key) => !(PRIMARY_DIET_FILTER_KEYS as readonly (keyof DietTypes)[]).includes(key),
    );
    const showExtendedDietPicker = hasExtendedDietFilterOptions();
    const characteristicsIsAll = filters.mealTypes.length === 0 && filters.lifestyleTags.length === 0;
    const categoriesIsAll = filters.categories.length === 0;
    const visibilityIsAll = filters.visibilitySelections.length === 0;

    const extraExcludeAllergens = filters.excludeAllergens.filter(
        (id) => !TOP_ALLERGY_IDS.includes(id),
    );

    const selectedChefs = filters.chefIds
        .map((id) => chefs.find((chef) => chef.id === id))
        .filter((chef): chef is Chef => chef != null);

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[500] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="relative bg-white dark:bg-slate-900 w-full max-w-md max-h-[min(85dvh,calc(100dvh-2rem))] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors"
                dir={textDir}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sticky header */}
                <div className="sticky top-0 z-20 flex justify-between items-center gap-3 p-4 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 min-w-0 truncate">
                        {t('filterAndSort')}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={handleHouseholdFit}
                            disabled={!canApplyHouseholdFit}
                            title={canApplyHouseholdFit ? t('householdFit') : t('householdFitEmpty')}
                            className={`h-8 px-3 rounded-full border flex items-center gap-1.5 text-xs font-bold transition-colors
                                ${canApplyHouseholdFit
                                    ? 'border-brand-sage/40 text-brand-sage bg-brand-sage/5 hover:bg-brand-sage/10 dark:bg-brand-sage/10'
                                    : 'border-gray-200 dark:border-slate-700 text-gray-300 dark:text-slate-600 cursor-not-allowed'}`}
                        >
                            <HouseholdIcon className="w-4 h-4 shrink-0" />
                            <span className="hidden sm:inline">{t('householdFit')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-slate-400 flex items-center justify-center hover:text-slate-800 dark:hover:text-slate-200 transition-colors shrink-0"
                            aria-label={t('cancel')}
                        >
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Scrollable chip wall */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-24 bg-white dark:bg-slate-900 scrollbar-none">
                    <FilterSection title={t('sortBy')} first>
                        <FilterChip
                            label={t('all')}
                            isActive={sortMode === 'all'}
                            onClick={() => setSortMode('all')}
                        />
                        <FilterChip
                            label={t('sort_recent')}
                            isActive={sortMode === 'recent'}
                            onClick={() => setSortMode('recent')}
                        />
                        <FilterChip
                            label={t('sort_rated')}
                            isActive={sortMode === 'rated'}
                            onClick={() => setSortMode('rated')}
                        />
                    </FilterSection>

                    <FilterSection title={t('filterByRating')}>
                        <FilterChip
                            label={t('all')}
                            isActive={filters.minRating == null}
                            onClick={() => setFilters((prev) => ({ ...prev, minRating: null }))}
                        />
                        {[1, 2, 3, 4, 5].map((stars) => (
                            <FilterChip
                                key={stars}
                                label={`⭐ ${t('filterRatingAndUp').replace('{stars}', String(stars))}`}
                                isActive={filters.minRating === stars}
                                onClick={() => setFilters((prev) => ({
                                    ...prev,
                                    minRating: prev.minRating === stars ? null : stars,
                                }))}
                            />
                        ))}
                    </FilterSection>

                    <section className="mt-6">
                        <h4 className="text-sm font-bold text-slate-400 dark:text-slate-500 mb-3 mt-2">
                            {t('dietaryPreferences')}
                        </h4>
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <FilterChip
                                label={t('all')}
                                isActive={dietIsAll}
                                onClick={() => setFilters((prev) => ({ ...prev, dietTypes: [] }))}
                            />
                            {PRIMARY_DIET_FILTER_KEYS.map((key) => {
                                const config = DIET_TYPE_CONFIG.find((item) => item.key === key);
                                if (!config) return null;
                                return (
                                    <FilterChip
                                        key={key}
                                        label={`${config.emoji} ${t(config.labelKey as Parameters<typeof getTranslation>[1])}`}
                                        isActive={filters.dietTypes.includes(key)}
                                        onClick={() => toggleDietType(key)}
                                    />
                                );
                            })}
                            {extraDietTypes.map((key) => {
                                const config = DIET_TYPE_CONFIG.find((item) => item.key === key);
                                if (!config) return null;
                                return (
                                    <span
                                        key={key}
                                        className="h-9 px-3 rounded-full border flex flex-row items-center gap-1.5 text-sm font-medium bg-brand-sage/10 border-brand-sage text-brand-sage shrink-0"
                                    >
                                        <span>{config.emoji}</span>
                                        <span className="truncate max-w-[120px]">
                                            {t(config.labelKey as Parameters<typeof getTranslation>[1])}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeDietType(key)}
                                            className="p-0.5 rounded-full hover:bg-brand-sage/20 transition-colors shrink-0"
                                            aria-label={t('clearSearch')}
                                        >
                                            <XIcon className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                );
                            })}
                            {showExtendedDietPicker && (
                                <button
                                    type="button"
                                    onClick={() => setIsDietPickerOpen(true)}
                                    className="h-9 px-4 rounded-full border border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 flex flex-row items-center gap-1.5 text-sm font-medium transition-colors shrink-0"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    {filters.dietTypes.length > 0 ? t('addDiet') : t('allDiets')}
                                </button>
                            )}
                        </div>
                    </section>

                    <section className="mt-6">
                        <div className="flex items-center gap-2 mb-3">
                            <h4 className="text-sm font-bold text-slate-400 dark:text-slate-500">
                                {t('suitableForAllergies')}
                            </h4>
                            <button
                                type="button"
                                onClick={() => setIsAllergyInfoOpen(true)}
                                className="w-6 h-6 rounded-full bg-gray-50 dark:bg-slate-800 text-gray-400 dark:text-slate-500 flex items-center justify-center hover:text-brand-sage transition-colors shrink-0"
                                aria-label={t('allergyFilterInfoTitle')}
                            >
                                <InfoIcon className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        <div className="flex flex-row flex-wrap items-center gap-2">
                            {TOP_ALLERGY_IDS.map((id) => {
                                const config = getAllergyConfig(id);
                                if (!config) return null;
                                return (
                                    <FilterChip
                                        key={id}
                                        label={`${config.emoji} ${t(config.labelKey as Parameters<typeof getTranslation>[1])}`}
                                        isActive={filters.excludeAllergens.includes(id)}
                                        onClick={() => toggleExcludeAllergen(id)}
                                    />
                                );
                            })}
                            {extraExcludeAllergens.map((id) => {
                                const config = getAllergyConfig(id);
                                if (!config) return null;
                                return (
                                    <span
                                        key={id}
                                        className="h-9 px-3 rounded-full border flex flex-row items-center gap-1.5 text-sm font-medium bg-brand-sage/10 border-brand-sage text-brand-sage shrink-0"
                                    >
                                        <span>{config.emoji}</span>
                                        <span className="truncate max-w-[120px]">
                                            {t(config.labelKey as Parameters<typeof getTranslation>[1])}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeExcludeAllergen(id)}
                                            className="p-0.5 rounded-full hover:bg-brand-sage/20 transition-colors shrink-0"
                                            aria-label={t('clearSearch')}
                                        >
                                            <XIcon className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                );
                            })}
                            <button
                                type="button"
                                onClick={() => setIsAllergyPickerOpen(true)}
                                className="h-9 px-4 rounded-full border border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 flex flex-row items-center gap-1.5 text-sm font-medium transition-colors shrink-0"
                            >
                                <PlusIcon className="w-4 h-4" />
                                {extraExcludeAllergens.length > 0 || filters.excludeAllergens.length > 0
                                    ? t('addAllergy')
                                    : t('allAllergies')}
                            </button>
                        </div>
                    </section>

                    <FilterSection title={t('category')}>
                        <FilterChip
                            label={t('all')}
                            isActive={categoriesIsAll}
                            onClick={() => setFilters((prev) => ({ ...prev, categories: [] }))}
                        />
                        {RECIPE_CATEGORIES.filter((category) => category !== 'General').map((category) => (
                            <FilterChip
                                key={category}
                                label={t(getRecipeCategoryTranslationKey(category) as Parameters<typeof getTranslation>[1])}
                                isActive={filters.categories.includes(category)}
                                onClick={() => toggleCategory(category)}
                            />
                        ))}
                    </FilterSection>

                    <FilterSection title={t('characteristics')}>
                        <FilterChip
                            label={t('all')}
                            isActive={characteristicsIsAll}
                            onClick={() => setFilters((prev) => ({ ...prev, mealTypes: [], lifestyleTags: [] }))}
                        />
                        {MEAL_TYPE_OPTIONS.map((mealType) => (
                            <FilterChip
                                key={mealType}
                                label={`${mealTypeEmoji(mealType)} ${mealTypeLabel(mealType)}`}
                                isActive={filters.mealTypes.includes(mealType)}
                                onClick={() => toggleMealType(mealType)}
                            />
                        ))}
                        {LIFESTYLE_TAG_CONFIG.map(({ key, labelKey, emoji }) => (
                            <FilterChip
                                key={key}
                                label={`${emoji} ${t(labelKey as Parameters<typeof getTranslation>[1])}`}
                                isActive={filters.lifestyleTags.includes(key)}
                                onClick={() => toggleLifestyleTag(key)}
                            />
                        ))}
                    </FilterSection>

                    <FilterSection title={t('visibilityLabel')}>
                        <FilterChip
                            label={t('all')}
                            isActive={visibilityIsAll}
                            onClick={() => setFilters((prev) => ({ ...prev, visibilitySelections: [] }))}
                        />
                        {VISIBILITY_CHIPS.map(({ key, labelKey }) => (
                            <FilterChip
                                key={key}
                                label={t(labelKey as Parameters<typeof getTranslation>[1])}
                                isActive={filters.visibilitySelections.includes(key)}
                                onClick={() => toggleVisibility(key)}
                            />
                        ))}
                    </FilterSection>

                    {/* Chef tokens */}
                    <section className="mt-6 mb-8">
                        <h4 className="text-sm font-bold text-slate-400 dark:text-slate-500 mb-3">
                            {t('filterByChef')}
                        </h4>
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            {selectedChefs.map((chef) => (
                                <span
                                    key={chef.id}
                                    className="h-9 px-3 rounded-full border flex flex-row items-center gap-1.5 text-sm font-medium bg-brand-sage/10 border-brand-sage text-brand-sage shrink-0"
                                >
                                    <span className="truncate max-w-[140px]">
                                        {getChefDisplayName(chef, language ?? 'en')}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeChef(chef.id)}
                                        className="p-0.5 rounded-full hover:bg-brand-sage/20 transition-colors shrink-0"
                                        aria-label={t('clearSearch')}
                                    >
                                        <XIcon className="w-3.5 h-3.5" />
                                    </button>
                                </span>
                            ))}
                            <button
                                type="button"
                                onClick={() => setIsChefModalOpen(true)}
                                className="h-9 px-4 rounded-full border border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 flex flex-row items-center gap-1.5 text-sm font-medium transition-colors shrink-0"
                            >
                                <PlusIcon className="w-4 h-4" />
                                {t('addChef')}
                            </button>
                        </div>
                    </section>
                </div>

                {/* Sticky footer */}
                <div className="absolute bottom-0 w-full p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 flex justify-between items-center gap-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20">
                    <button
                        type="button"
                        onClick={handleClearAll}
                        className="text-slate-500 dark:text-slate-400 font-medium text-sm hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    >
                        {t('clearAll')}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        className="bg-brand-orange text-white px-8 py-3 rounded-xl font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-lg shadow-brand-orange/20"
                    >
                        {t('save')}
                    </button>
                </div>
            </div>

            <ChefFilterModal
                isOpen={isChefModalOpen}
                onClose={() => setIsChefModalOpen(false)}
                chefs={chefs}
                recipes={recipes}
                users={allUsers}
                selectedChefIds={filters.chefIds}
                onApply={(chefIds) => setFilters((prev) => ({ ...prev, chefIds }))}
                language={language}
            />
            <AllergyFilterInfoModal
                isOpen={isAllergyInfoOpen}
                onClose={() => setIsAllergyInfoOpen(false)}
                language={language}
            />
            <AllergyFilterPickerModal
                isOpen={isAllergyPickerOpen}
                onClose={() => setIsAllergyPickerOpen(false)}
                selectedAllergens={filters.excludeAllergens}
                onApply={(excludeAllergens) => setFilters((prev) => ({ ...prev, excludeAllergens }))}
                language={language}
            />
            <DietFilterPickerModal
                isOpen={isDietPickerOpen}
                onClose={() => setIsDietPickerOpen(false)}
                selectedDietTypes={filters.dietTypes}
                onApply={(dietTypes) => setFilters((prev) => ({ ...prev, dietTypes }))}
                language={language}
            />
        </div>,
        document.body,
    );
};

export type { FilterState };
export default RecipeFilterModal;
